import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { INSPECTION_TONE_STYLES, type InspectionChipView, type InspectionTone } from '@/lib/inspections';

/**
 * The pill every inspection label is drawn in: a grade, a result, a condition, a state. The house
 * `Badge` (outline, as the stage badge beside it), coloured by tone.
 */
export function InspectionPill({ tone, children, className }: { tone: InspectionTone; children: ReactNode; className?: string }) {
    return (
        <Badge variant="outline" className={cn('gap-1 whitespace-nowrap', INSPECTION_TONE_STYLES[tone], className)}>
            {children}
        </Badge>
    );
}

interface InspectionChipProps {
    chip: InspectionChipView;
    /** The car's inspections page. Without it the chip is a plain label. */
    to?: string;
    /** Which car and contract the link opens, for its accessible name: 'GR 1234-26, contract #120'. */
    subject: string;
}

/**
 * The inspection chip beside a contract row's stage badge. The clipboard mark (a list, not a tick:
 * it sits on Held and Missed too) tells it apart from the stage ("Active"). As a link it carries a
 * 44px hit area that the negative margin keeps out of the row's height, so a thumb on a phone
 * reaches it without having to land on the 20px pill itself.
 */
export function InspectionChip({ chip, to, subject }: InspectionChipProps) {
    const pill = (
        <InspectionPill tone={chip.tone}>
            <ClipboardList className="h-3 w-3 shrink-0" aria-hidden="true" />
            {chip.label}
        </InspectionPill>
    );

    if (!to) return pill;

    const name = chip.key === 'open'
        ? `Open the inspections for ${subject}`
        : `Inspection ${chip.label}. Open the inspections for ${subject}`;

    return (
        <Link
            to={to}
            aria-label={name}
            className="-my-3 inline-flex min-h-11 items-center rounded-full px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
            {pill}
        </Link>
    );
}
